/** Frontend accessor for the hierarchical relationship taxonomy.
 *
 *  The canonical tree lives on the backend
 *  (backend/src/lib/relationship-taxonomy.ts) and is served by
 *  GET /api/relationships/taxonomy. This module fetches it once,
 *  memoizes it process-wide, and exposes a small lookup object so
 *  chips can render "Wife" with a "Family" parent label and pickers
 *  can group by root — without each component re-fetching or a
 *  hand-mirrored copy drifting out of sync.
 *
 *  Until the fetch resolves, helpers degrade gracefully:
 *  humanize() title-cases the slug and chain() returns just the slug
 *  (no parent label) so nothing renders blank. */

import { useEffect, useState } from "react";
import { api, type TaxonomyEntry } from "./api";

export interface TaxonomyLookup {
  byType: Map<string, TaxonomyEntry>;
  /** Top-level roots in sort order (family / work / …), is_user_facing. */
  roots: TaxonomyEntry[];
  /** Direct children of a type, sorted. */
  childrenOf: (type: string) => TaxonomyEntry[];
  /** Every user-facing entry in a root's subtree (root inclusive),
   *  sorted by sort_order — for grouped <optgroup> pickers. */
  userFacingDescendants: (rootType: string) => TaxonomyEntry[];
  /** leaf → … → root slug chain. Unknown slug → [slug]. */
  chain: (type: string | null | undefined) => string[];
  /** Top-level root entry for a slug, or undefined. */
  rootOf: (type: string | null | undefined) => TaxonomyEntry | undefined;
  /** Human label; falls back to title-cased underscores. */
  humanize: (type: string | null | undefined, custom?: string | null) => string;
  loaded: boolean;
}

let _cache: TaxonomyEntry[] | null = null;
let _inflight: Promise<TaxonomyEntry[]> | null = null;

function fetchTree(): Promise<TaxonomyEntry[]> {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = api
    .relationshipTaxonomy()
    .then((d) => {
      _cache = d.tree ?? [];
      return _cache;
    })
    .catch(() => {
      _cache = [];
      return _cache;
    });
  return _inflight;
}

function titleCase(slug: string): string {
  return slug
    .split("_")
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : s))
    .join(" ");
}

function buildLookup(tree: TaxonomyEntry[], loaded: boolean): TaxonomyLookup {
  const byType = new Map<string, TaxonomyEntry>(tree.map((e) => [e.type, e]));
  const children = new Map<string, TaxonomyEntry[]>();
  for (const e of tree) {
    if (e.parent_type) {
      const arr = children.get(e.parent_type) ?? [];
      arr.push(e);
      children.set(e.parent_type, arr);
    }
  }
  for (const arr of children.values()) {
    arr.sort((a, b) => a.sort_order - b.sort_order);
  }
  const chain = (type: string | null | undefined): string[] => {
    if (!type) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    let cur = byType.get(type);
    if (!cur) return [type];
    while (cur && !seen.has(cur.type)) {
      seen.add(cur.type);
      out.push(cur.type);
      cur = cur.parent_type ? byType.get(cur.parent_type) : undefined;
    }
    return out;
  };
  const rootOf = (type: string | null | undefined): TaxonomyEntry | undefined => {
    const c = chain(type);
    return c.length ? byType.get(c[c.length - 1]) : undefined;
  };
  const humanize = (
    type: string | null | undefined,
    custom?: string | null,
  ): string => {
    if (custom && type === "custom") return custom;
    if (!type) return "—";
    const e = byType.get(type);
    if (e) return e.display_name;
    return titleCase(type);
  };
  const roots = tree
    .filter((e) => e.parent_type === null && e.is_user_facing === 1)
    .sort((a, b) => a.sort_order - b.sort_order);
  const userFacingDescendants = (rootType: string): TaxonomyEntry[] => {
    const out: TaxonomyEntry[] = [];
    const stack = [rootType];
    const seen = new Set<string>();
    while (stack.length) {
      const t = stack.pop()!;
      if (seen.has(t)) continue;
      seen.add(t);
      const e = byType.get(t);
      if (e && e.is_user_facing === 1) out.push(e);
      for (const c of children.get(t) ?? []) stack.push(c.type);
    }
    return out.sort((a, b) => a.sort_order - b.sort_order);
  };
  return {
    byType,
    roots,
    childrenOf: (t) => children.get(t) ?? [],
    userFacingDescendants,
    chain,
    rootOf,
    humanize,
    loaded,
  };
}

/** Hook: returns a memoized taxonomy lookup. Triggers the one-time
 *  fetch on first mount; re-renders the component once it lands. */
export function useRelationshipTaxonomy(): TaxonomyLookup {
  const [tree, setTree] = useState<TaxonomyEntry[] | null>(_cache);
  useEffect(() => {
    if (tree) return;
    let alive = true;
    fetchTree().then((t) => {
      if (alive) setTree(t);
    });
    return () => {
      alive = false;
    };
  }, [tree]);
  return buildLookup(tree ?? [], tree != null);
}
